import React from "react"

const EVENT_LABELS = {
  initiated: "Document initiated",
  observations_captured: "Observation evidence captured",
  submitted: "Submitted by admin",
  reinitiated: "Reinitiated by evaluator",
  completed: "Completed by evaluator",
}

const formatLogDateTime = (value) => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const parseActivityLog = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const buildFallbackEntries = (record) => {
  if (!record) return []

  const entries = []

  if (record.createdAt) {
    entries.push({ event: "initiated", at: record.createdAt })
  }

  if (record.reinitiatedAt) {
    entries.push({ event: "reinitiated", at: record.reinitiatedAt })
  }

  if (record.status === "submitted_by_admin" && record.updatedAt) {
    entries.push({ event: "submitted", at: record.updatedAt })
  }

  if (record.status === "completed" && record.updatedAt) {
    entries.push({ event: "completed", at: record.updatedAt })
  }

  if (record.status === "observations_captured" && record.updatedAt) {
    entries.push({ event: "observations_captured", at: record.updatedAt })
  }

  return entries.filter((entry) => entry.at)
}

const buildEntries = (record) => {
  if (!record) return []

  const stored = parseActivityLog(record.activityLog)
  if (stored.length) {
    return stored
      .filter((entry) => entry?.event && entry?.at)
      .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
  }

  return buildFallbackEntries(record).sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
  )
}

const FormActivityLog = ({ record }) => {
  const entries = buildEntries(record)

  if (!entries.length) return null

  return (
    <div
      className="mt-6 border-t border-gray-300 pt-4"
      aria-label="Document activity log"
    >
      <h3 className="text-sm font-semibold text-gray-800 mb-2">
        Document activity log
      </h3>
      <ul className="space-y-1.5">
        {entries.map((entry, index) => {
          const label = EVENT_LABELS[entry.event] || entry.event
          const when = formatLogDateTime(entry.at)
          return (
            <li
              key={`${entry.event}-${entry.at}-${index}`}
              className="text-xs text-gray-600 flex flex-wrap gap-x-2"
            >
              <span className="font-medium text-gray-800">{label}</span>
              <span aria-hidden="true">—</span>
              <time dateTime={entry.at}>{when || "—"}</time>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default FormActivityLog
